import { Box, Button, Dialog, IconButton, Typography } from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { routes } from "schemas/routes";

import { GAMIFICATION_UNLOCKED_ALL_FEATURES } from "utils/localStorage";
import { trackEvent } from "utils/tracking";

import styles from "./styles";

interface PremiumFeatureUnlockedProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade?: () => void;
}

const CONFETTI_COLORS = ["#F7EE6F", "#B1B3F8", "#014339", "#10B981"];

const CONFETTI_PARTICLES = Array.from({ length: 50 }, (_, i) => ({
  id: i,
  x: Math.random() * 100,
  duration: Math.random() * 2 + 2,
  color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
  size: Math.random() * 6 + 6,
  delay: Math.random() * 1.5,
}));

const BENEFITS = [
  {
    iconClass: "fa-solid fa-building",
    iconColor: "#16A34A",
    iconBg: "#DCFCE7",
    borderColor: "#BBF7D0",
    title: "500,000+ Premium Brands",
    description: "Access to our full database of verified brands",
  },
  {
    iconClass: "fa-solid fa-envelope",
    iconColor: "#2563EB",
    iconBg: "#DBEAFE",
    borderColor: "#BFDBFE",
    title: "Direct Brand Contacts",
    description: "Real email addresses and decision-makers",
  },
  {
    iconClass: "fa-solid fa-unlock",
    iconColor: "#7C3AED",
    iconBg: "#EDE9FE",
    borderColor: "#DDD6FE",
    title: "Full Platform Access",
    description: "All premium features and tools unlocked",
  },
];

const PremiumFeatureUnlocked = ({
  isOpen,
  onClose,
  onUpgrade,
}: PremiumFeatureUnlockedProps) => {
  const { currentOrg } = useContext(OrganizationUserContext);
  const navigate = useNavigate();
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (isOpen) {
      trackEvent("Gamification - Open Premium Feature Unlocked Dialog");
      setShowConfetti(true);
      localStorage.setItem(
        `${currentOrg?.id}-${GAMIFICATION_UNLOCKED_ALL_FEATURES}`,
        "true",
      );
      const timer = setTimeout(() => setShowConfetti(false), 4000);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      PaperProps={{ sx: styles.dialogPaper }}
    >
      {/* Confetti */}
      {showConfetti &&
        CONFETTI_PARTICLES.map((p) => (
          <Box
            key={p.id}
            sx={styles.confettiParticle(
              p.x,
              p.duration,
              p.color,
              p.size,
              p.delay,
            )}
          />
        ))}

      {/* Decorative sparkles */}
      <Box
        component="i"
        className="fa-solid fa-sparkles"
        sx={styles.sparkleTopRight}
      />
      <Box
        component="i"
        className="fa-solid fa-sparkles"
        sx={styles.sparkleBottomLeft}
      />

      {/* Close button */}
      <IconButton onClick={onClose} sx={styles.closeButton} size="small">
        <Box
          component="i"
          className="fa-solid fa-times"
          sx={{ fontSize: 16 }}
        />
      </IconButton>

      <Box sx={styles.content}>
        {/* Trophy */}
        <Box sx={styles.trophyWrapper}>
          <Box sx={{ position: "relative" }}>
            <Box sx={styles.trophyGlow} />
            <Box sx={styles.trophyCircle}>
              <Box
                component="i"
                className="fa-solid fa-trophy"
                sx={styles.trophyIcon}
              />
            </Box>
          </Box>
        </Box>

        {/* Title */}
        <Box sx={styles.titleSection}>
          <Typography sx={styles.title}>🎉 Premium Unlocked!</Typography>
          <Typography sx={styles.subtitle}>
            Amazing work! You've completed your onboarding and earned premium
            access.
          </Typography>
        </Box>

        {/* Benefits */}
        <Box sx={styles.benefitsSection}>
          {BENEFITS.map((benefit) => (
            <Box
              key={benefit.title}
              sx={styles.benefitCard(benefit.borderColor)}
            >
              <Box sx={styles.benefitIconBox(benefit.iconBg)}>
                <Box
                  component="i"
                  className={benefit.iconClass}
                  sx={styles.benefitIcon(benefit.iconColor)}
                />
              </Box>
              <Box>
                <Typography sx={styles.benefitTitle}>
                  {benefit.title}
                </Typography>
                <Typography sx={styles.benefitDescription}>
                  {benefit.description}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Stats */}
        <Box sx={styles.statsBox}>
          <Typography sx={styles.statsTitle}>
            🎉 You've completed all onboarding steps!
          </Typography>
          <Typography sx={styles.statsSubtitle}>
            You now have full access to premium features. Keep going with
            challenges to unlock even more achievements!
          </Typography>
        </Box>

        {/* CTA */}
        <Box sx={styles.ctaSection}>
          <Button
            variant="contained"
            disableElevation
            onClick={() => {
              navigate(`/${routes.brands}`);
              onClose();
            }}
            sx={styles.primaryBtn}
          >
            Start Pitching to Premium Brands →
          </Button>

          {onUpgrade && (
            <>
              <Box sx={styles.dividerRow}>
                <Box sx={styles.dividerLine} />
                <Typography sx={styles.dividerText}>Or</Typography>
                <Box sx={styles.dividerLine} />
              </Box>

              <Box>
                <Button
                  variant="outlined"
                  onClick={() => {
                    onUpgrade();
                    onClose();
                  }}
                  sx={styles.secondaryBtn}
                  fullWidth
                >
                  <Box
                    component="i"
                    className="fa-solid fa-credit-card"
                    sx={{ fontSize: 14, mr: 1 }}
                  />
                  Upgrade to Get more monthly pitches
                  <Box
                    component="i"
                    className="fa-solid fa-bolt"
                    sx={{ fontSize: 14, ml: 1, color: "#F7EE6F" }}
                  />
                </Button>
              </Box>
            </>
          )}
        </Box>

        {/* Encouragement */}
        <Typography sx={styles.encouragement}>
          💪 Remember: Consistent outreach is the key to landing deals. Keep
          pitching!
        </Typography>
      </Box>
    </Dialog>
  );
};

export default PremiumFeatureUnlocked;
